import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { WebSocketAction } from 'utils';
import { RequestState } from './enum/RequestState';
import {
  WebSocketState,
  WS_COLLECTING_CONNECT,
  WS_CONNECT,
  WS_DISCONNECT,
  WS_INACTIVITY_DISCONNECT,
  WS_NAMESPACE,
} from './WebSocket.types';

// Init state
const initialState: WebSocketState = {
  connected: false,
  collectingConnected: false,
  requests: new Map<string, WebSocketAction>(),
  processedRequests: new Map<string, Date>(), // todo create periodical cleanup - delete everything older than 5 minutes
  countErrorNoConnectionToBESide: 0,
  wsUserSessionInitialized: false,
  wsConnectInitialized: false,
  wsHasEverConnected: false,
};

// Middleware actions
export const wsConnect = createAction(WS_CONNECT);
export const wsDisconnect = createAction(WS_DISCONNECT);
export const wsInactivityDisconnect = createAction(WS_INACTIVITY_DISCONNECT);
export const wsCollectingConnect = createAction(WS_COLLECTING_CONNECT);

// Slice
const slice = createSlice({
  name: WS_NAMESPACE,
  initialState,
  reducers: {
    setConnected: (state, { payload }: PayloadAction<boolean>) => {
      state.connected = payload;
    },
    setCollectingConnected: (state, { payload }: PayloadAction<boolean>) => {
      state.collectingConnected = payload;
    },
    addRequest: (state, { payload }: PayloadAction<WebSocketAction>) => {
      state.requests.set(payload.traceId, payload);
    },
    addProcessedRequest: (state, { payload }: PayloadAction<string>) => {
      state.processedRequests.set(payload, new Date());
    },
    setWsUserSessionInitialized: (state, action: PayloadAction<boolean>) => {
      state.wsUserSessionInitialized = action.payload;
    },
    setWsConnectInitialized: (state, action: PayloadAction<boolean>) => {
      state.wsConnectInitialized = action.payload;
    },
    setWsHasEverConnected: (state, action: PayloadAction<boolean>) => {
      state.wsHasEverConnected = action.payload;
    },
    removeRequest: (state, { payload }: PayloadAction<string>) => {
      state.requests.delete(payload);
    },
    removeProcessedRequest: (state, { payload }: PayloadAction<string>) => {
      state.processedRequests.delete(payload);
    },
    setErrorNoConnectionToBESide: (
      state,
      { payload }: PayloadAction<{ connectionUnavailable: boolean; handleCount?: boolean }>,
    ) => {
      const { connectionUnavailable, handleCount } = payload;
      //ONEPORTAL-3685
      if (connectionUnavailable) {
        const toAdd = handleCount ? 1 : 3;
        state.countErrorNoConnectionToBESide += toAdd;
      } else {
        state.countErrorNoConnectionToBESide = 0;
      }
    },
  },
});

// Actions
export const {
  setConnected,
  setCollectingConnected,
  addRequest,
  addProcessedRequest,
  removeRequest,
  removeProcessedRequest,
  setErrorNoConnectionToBESide,
  setWsUserSessionInitialized,
  setWsConnectInitialized,
  setWsHasEverConnected,
} = slice.actions;

// Getters/Selectors
export const isConnected = createSelector(
  (state: RootState) => state.websockets.connected,
  (connected) => connected,
);

export const isCollectingConnected = createSelector(
  (state: RootState) => state.websockets.collectingConnected,
  (collectingConnected) => collectingConnected,
);

export const getRequests = createSelector(
  (state: RootState) => state.websockets.requests,
  (requests) => requests,
);

export const getProcessedRequests = createSelector(
  (state: RootState) => state.websockets.processedRequests,
  (processedRequests) => processedRequests,
);

export const getRequest = createSelector(
  getRequests,
  (_state: RootState, trackingId: string) => trackingId,
  (requests, trackingId) => requests.get(trackingId),
);

export const getProcessedRequest = createSelector(
  getProcessedRequests,
  (_state: RootState, trackingId: string) => trackingId,
  (processedRequests, trackingId) => processedRequests.get(trackingId),
);

export const getPendingRequests = createSelector(getRequests, (requests) =>
  [...requests.values()].filter((r) => r.state === RequestState.PENDING),
);

export const getPendingRequestsSorted = createSelector(getPendingRequests, (pending) =>
  [...pending].sort((a: WebSocketAction, b: WebSocketAction) => a.created.getTime() - b.created.getTime()),
);

export const getErrorNoConnectionToBESide = createSelector(
  (state: RootState) => state.websockets.countErrorNoConnectionToBESide,
  (countErrorNoConnectionToBESide) => countErrorNoConnectionToBESide >= 3,
);

export const isWsUserSessionInitialized = createSelector(
  (state: RootState) => state.websockets.wsUserSessionInitialized,
  (wsUserSessionInitialized) => wsUserSessionInitialized,
);

export const getIsWsConnectInitialized = createSelector(
  (state: RootState) => state.websockets.wsConnectInitialized,
  (wsConnectInitialized) => wsConnectInitialized,
);

export const getIsWsHasEverConnected = createSelector(
  (state: RootState) => state.websockets.wsHasEverConnected,
  (wsHasEverConnected) => wsHasEverConnected,
);

// Export reducer
export default slice.reducer;
